/**
 * `projectDto.ts` — wire shape of a Campfire project as it lives in
 * Dynamo / Unity's `Models/Data/ProjectData.cs`.
 *
 * Mirrors the public properties of `ProjectData` and the Dynamo
 * attribute names from `DynamoClient.ToProjectData` / `ToProjectItem`
 * (`shared-application-code/.../AWS/DynamoClient.cs`). Field names are
 * kept PascalCase to match the upstream JSON exactly so a marshalled
 * `unmarshall(item)` from `@aws-sdk/util-dynamodb` lines up 1:1.
 *
 * Rule of thumb: only the fields the web client actually reads are
 * declared as required. Unknown / future Dynamo attributes pass
 * through `[k: string]: unknown` so we never lose data on round-trip.
 */

export type PublishingStatus =
  | "Draft"
  | "NeedsReview"
  | "InReview"
  | "ReadyToPublish"
  | "Published";

/**
 * Mirrors `Models/Data/SharedUserRole.cs`. Names match the Unity enum
 * casing so JSON parses straight through.
 */
export type SharedUserRoleName =
  | "Owner"
  | "Editor"
  | "Commenter"
  | "Viewer"
  | "NoAccess"
  | "Presenter";

export interface ProjectDto {
  /** Unity `Name`. */
  readonly Name: string;
  /** Unity `UID` — the canonical project id used everywhere. */
  readonly UID: string;
  readonly Author: string;
  readonly CreatorId: string;
  readonly CreatedTimestamp: string;
  readonly ModifiedTimestamp: string;
  readonly AccountRole?: SharedUserRoleName | number;
  readonly AnchorOwner?: string;
  /**
   * Unity `ProjectData.SerializationVersion`: V1 = Dynamo parts/snapshots,
   * V2 = gzip JSON bases in S3 plus Dynamo snapshot overrides.
   */
  readonly SerializationVersion?: number;
  readonly AssetIDs?: ReadonlyArray<string>;
  readonly DocumentStoragePaths?: ReadonlyArray<string>;
  readonly MaterialPresets?: ReadonlyArray<string>;
  readonly IsDuplicating?: boolean;
  readonly SupportsInstances?: boolean;
  readonly PublishingStatus?: PublishingStatus | number;
  readonly SupportedDeviceMask?: number;
  readonly ProjectPrompt?: string;
  /**
   * Per-project user display settings (2026-07 upstream merge, AI-925).
   * Dynamo attrs on the project row; absent means `false`. Consumed by
   * M12 presence rendering (`NetworkUsersRenderer` hides avatars /
   * name tags) and the `update_project` live event.
   */
  readonly HideAvatars?: boolean;
  readonly HideUserTags?: boolean;
  /**
   * AI-1288 project-level console model. Missing/empty keeps the bundled
   * default console; otherwise this is a customer-scoped AssetData ID.
   */
  readonly ConsoleAssetID?: string;

  /**
   * Pass-through bucket for any Dynamo attribute we haven't surfaced
   * yet — keeps round-trip safe without forcing a schema bump every
   * time Unity adds a new field.
   */
  readonly [extra: string]: unknown;
}

/**
 * Author display names that mark a project as a "sample / Learn"
 * project rather than a user-created one. Mirrors
 * `ProjectInfo.IsUserCreatedProject` in
 * `Models/CustomerModel/ProjectInfo.cs` (constants
 * `k_sampleAppAuthorNameOne/Two/Three`).
 *
 * IMPORTANT: do NOT rename / reorder — Unity ships these strings
 * exact-match.
 */
export const SAMPLE_PROJECT_AUTHORS: ReadonlySet<string> = new Set([
  "Campf1reS",
  "Campfire User",
  "Spark",
]);

/**
 * Returns `true` when the project's `Author` is NOT one of the
 * sample-project authors — i.e. the row counts as either Drafts or
 * Shared (per Unity's `IsUserCreatedProject`).
 */
export function isUserCreatedProject(dto: Pick<ProjectDto, "Author">): boolean {
  return !SAMPLE_PROJECT_AUTHORS.has(dto.Author);
}

/**
 * Mirrors `ProjectInfo.IsShared` (Unity):
 *   `AccountRole != NoAccess || HasSharedLinks`.
 *
 * The web port doesn't have shared-link metadata yet (lands in M9 with
 * sharing), so we just check `AccountRole`. Shared-link wiring will
 * extend this without changing callers.
 */
export function isSharedProject(dto: Pick<ProjectDto, "AccountRole">): boolean {
  const role = dto.AccountRole;
  // Unity's Dynamo reader defaults missing AccountRole to Editor, so a
  // missing legacy value behaves as shared there.
  if (role === undefined || role === null) return true;
  if (typeof role === "string") return role !== "NoAccess";
  // Unity enum order: Owner=0, Editor=1, Commenter=2, Viewer=3,
  // NoAccess=4, Presenter=5.
  return role !== 4;
}
